import { VASTClient } from '@dailymotion/vast-client'

/** Shared client — follows wrapper chains (up to wrapperLimit). */
export const reelsVastClient = new VASTClient()

const WRAPPER_LIMIT = 5
/** Per request (each wrapper is one request). The library default is 120 s. */
const VAST_TIMEOUT_MS = 3000

export type VastLinearCreative = {
  type?: string
  duration?: number
  mediaFiles?: Array<{
    fileURL: string
    height: number
    width: number
    mimeType?: string
    bitrate?: number
  }>
  videoClickThroughURLTemplate?: { url?: string } | string | null
}

export type VastParsedAd = {
  id?: string
  hasFailed?: boolean
  creatives?: VastLinearCreative[]
  errorURLTemplates?: string[]
}

export function resolveVastRequestUrl(pathOrUrl: string): string {
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
    return pathOrUrl
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${pathOrUrl}`
  }
  return pathOrUrl
}

export function getLinearCreative(ad: VastParsedAd): VastLinearCreative | null {
  const linear = ad.creatives?.find(c => c.type === 'linear')
  return linear ?? null
}

export function getClickThroughUrl(linear: VastLinearCreative): string {
  const template = linear.videoClickThroughURLTemplate
  if (!template) {
    return ''
  }
  if (typeof template === 'string') {
    return template
  }
  return template.url ?? ''
}

export async function fetchAndParseVast(tagUrl: string) {
  const url = resolveVastRequestUrl(tagUrl)
  return reelsVastClient.get(url, {
    wrapperLimit: WRAPPER_LIMIT,
    timeout: VAST_TIMEOUT_MS,
  })
}

/** what the parser saw on the way, which the response itself drops */
export interface IVastFetchDetails {
  /** the last VAST error code it reported (a wrapper that failed or timed out) */
  errorCode: number | null
  /** the outermost ad when GAM redirects: its own line item and creative */
  wrapper: { adId: string; creativeId: string } | null
}

const idOf = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : ''

/**
 * One request on a client of its own (its parser's events are then only
 * this request's), with how long each hop may take
 */
export async function fetchVastWithDetails(
  tagUrl: string,
  timeoutMs = VAST_TIMEOUT_MS
) {
  const client = new VASTClient()
  const parser = client.getParser()
  const details: IVastFetchDetails = { errorCode: null, wrapper: null }
  const onError = (data: { ERRORCODE?: unknown } | undefined) => {
    const code = Number(data?.ERRORCODE)
    if (Number.isFinite(code)) {
      details.errorCode = code
    }
  }
  // the first hop away from the tag leaves from GAM's own <Ad>
  const onResolving = (data: { wrapperAd?: unknown } | undefined) => {
    const ad = data?.wrapperAd as
      | { id?: unknown; creatives?: { id?: unknown }[] }
      | null
      | undefined
    if (details.wrapper || !ad) {
      return
    }
    const creatives = Array.isArray(ad.creatives) ? ad.creatives : []
    details.wrapper = {
      adId: idOf(ad.id),
      creativeId: idOf(creatives[0]?.id),
    }
  }
  parser.on('VAST-error', onError)
  parser.on('VAST-resolving', onResolving)
  try {
    const response = await client.get(resolveVastRequestUrl(tagUrl), {
      wrapperLimit: WRAPPER_LIMIT,
      timeout: timeoutMs,
    })
    return { response, details }
  } finally {
    parser.off('VAST-error', onError)
    parser.off('VAST-resolving', onResolving)
  }
}

export function fireVastError(
  ad: VastParsedAd | null | undefined,
  errorCode: string
) {
  if (!ad?.errorURLTemplates?.length) {
    return
  }
  const templates = ad.errorURLTemplates
  templates.forEach(template => {
    const pixelUrl = template.replace(/\[ERRORCODE]/g, errorCode)
    if (pixelUrl) {
      fetch(pixelUrl, { mode: 'no-cors', keepalive: true }).catch(() => {
        console.error('Error firing VAST error', pixelUrl)
      })
    }
  })
}
